import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { getErrorMessage } from "utils/errorHelpers";
import type { TransitionToTrialRequestDto } from "client/api-v2";
import DatePickerInput from "components/DatePickerInput/DatePickerInput";

const DefaultTrialDurationDays = 30;

interface TransitionToTrialDialogProps {
  open: boolean;
  subscriptionName: string;
  currentLifecycleType: string;
  handleClose: () => void;
  submitTransition: (request: TransitionToTrialRequestDto) => Promise<void>;
}

export function TransitionToTrialDialog({
  open,
  subscriptionName,
  currentLifecycleType,
  handleClose,
  submitTransition,
}: TransitionToTrialDialogProps) {
  const defaultStart = () => DateTime.now().startOf("day");
  const defaultEnd = () => DateTime.now().startOf("day").plus({ days: DefaultTrialDurationDays });

  const [termStartDate, setTermStartDate] = useState<DateTime>(defaultStart());
  const [termEndDate, setTermEndDate] = useState<DateTime>(defaultEnd());
  const [reason, setReason] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setTermStartDate(defaultStart());
      setTermEndDate(defaultEnd());
      setReason("");
      setError(undefined);
    }
  }, [open]);

  const isValid = useMemo(() => {
    if (!reason.trim() || reason.length > 500) return false;
    if (!termStartDate || !termEndDate) return false;
    if (termStartDate < DateTime.now().startOf("day")) return false;
    if (termEndDate <= termStartDate) return false;
    if (termEndDate > DateTime.now().startOf("day").plus({ years: 1 })) return false;
    return true;
  }, [termStartDate, termEndDate, reason]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(undefined);
    try {
      const request: TransitionToTrialRequestDto = {
        termStartDate: termStartDate.toISODate() ?? "",
        termEndDate: termEndDate.toISODate() ?? "",
        reason: reason.trim(),
      };
      await submitTransition(request);
      handleClose();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Transition to Trial</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid size={12}>
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Subscription:</strong> {subscriptionName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                <strong>Current Lifecycle:</strong> {currentLifecycleType}
              </Typography>
            </Box>
          </Grid>

          <Grid size={12}>
            <Alert severity="info">
              The subscription will be moved to the current <strong>Enterprise</strong> plan with the enterprise trial
              entitlement overrides applied, and the license regenerated. No marketing or trial-start integration events
              will be published.
            </Alert>
          </Grid>

          {error && (
            <Grid size={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <DatePickerInput
              label="Term Start Date"
              value={termStartDate}
              onChange={(date) => setTermStartDate(date ?? defaultStart())}
              showLocalConversion={true}
              minDate={DateTime.now().startOf("day")}
              required
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <DatePickerInput
              label="Term End Date"
              value={termEndDate}
              onChange={(date) => setTermEndDate(date ?? defaultEnd())}
              showLocalConversion={true}
              minDate={termStartDate.plus({ days: 1 })}
              maxDate={DateTime.now().startOf("day").plus({ years: 1 })}
              required
            />
          </Grid>

          <Grid size={12}>
            <TextField
              autoFocus
              required
              fullWidth
              multiline
              rows={4}
              label="Reason"
              placeholder="Why is this customer being moved to a trial?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              error={!!error && !reason.trim()}
              helperText={
                reason.length > 500
                  ? `Reason is too long (${reason.length}/500 characters)`
                  : !reason.trim() && !!error
                  ? "Reason is required"
                  : ""
              }
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting || !isValid}>
          {isSubmitting ? "Transitioning..." : "Transition to Trial"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
